import * as React from 'react'
import { clsx } from 'clsx'
import { VariantProps, cva } from 'class-variance-authority'
import s from './input.module.scss'
import Link from 'next/link'
import { Slot } from '@radix-ui/react-slot'
import { IconButton } from '../icon-button'
import { CaretSortIcon } from '@radix-ui/react-icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../dropdown-menu'

export const $input = cva(
  clsx(
    s['input'],
    'rounded-md border text-dark-bright focus:outline-none w-full placeholder:select-none disabled:!cursor-text disabled:opacity-50'
  ),
  {
    variants: {
      intent: {
        primary:
          'border-dark-border-solid bg-dark-shade placeholder:text-dark-faint',
        secondary:
          'bg-dark-border-solid border-dark-elevated placeholder:text-dark-faint',
      },
      iconPaddingLeft: { true: '!pl-7' },
      size: {
        xs: 'h-7 text-xs px-2',
        sm: 'h-8 text-sm px-2',
        md: 'h-9 text-sm px-3',
      },
      invalid: {
        true: 'border-dark-error ring-dark-error ring-1 focus:ring-dark-error focus:border-dark-error',
      },
    },
    defaultVariants: {
      intent: 'primary',
      size: 'md',
    },
  }
)

export type InputProps = Omit<
  React.JSX.IntrinsicElements['input'],
  'ref' | 'size'
> & {
  ref?: React.LegacyRef<HTMLInputElement>
  asChild?: boolean
} & (
    | {
        icon: React.ReactNode
        containerClassName?: string
      }
    | {
        icon?: never
        containerClassName?: never
      }
  ) &
  VariantProps<typeof $input>

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      size,
      intent,
      invalid,
      icon,
      iconPaddingLeft,
      asChild,
      containerClassName,
      ...rest
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'input'

    if (icon) {
      return (
        <div className={clsx('relative', containerClassName)}>
          {icon && (
            <span className="left-2 top-1/2 -translate-y-1/2 flex-shrink-0 absolute pointer-events-none">
              {icon}
            </span>
          )}
          <Comp
            {...rest}
            className={clsx(
              $input({
                size,
                intent,
                className,
                iconPaddingLeft: iconPaddingLeft ?? Boolean(icon),
                invalid,
              })
            )}
            ref={ref}
          />
        </div>
      )
    }
    return (
      <Comp
        {...rest}
        className={clsx(
          $input({
            size,
            intent,
            className,
            invalid,
          }),
          s['input']
        )}
        ref={ref}
      />
    )
  }
)

export const $label = cva('text-dark-muted', {
  variants: {
    blocked: {
      true: 'p-2 py-4 -mx-2 rounded-xl',
      false: '',
    },
    horizontal: {
      reverse: 'flex flex-row-reverse gap-x-2 items-center justify-end',
      true: 'flex gap-x-2 items-center w-full [&>span]:mr-auto',
      false: 'block',
    },
    untitled: {
      true: '[&>span]:opacity-40',
      false: '',
    },
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
    },
  },
  compoundVariants: [
    {
      horizontal: false,
      size: 'xs',
      class: '[&>span]:pb-1.5',
    },
    {
      horizontal: false,
      size: ['sm', 'md'],
      class: '[&>span]:pb-2',
    },
  ],
  defaultVariants: {
    blocked: true,
    horizontal: false,
    untitled: false,
    size: 'sm',
  },
})

export type LabelProps = Omit<React.JSX.IntrinsicElements['label'], 'ref'> & {
  ref?: React.Ref<HTMLLabelElement>
  label: React.ReactNode
  onLabelDoubleClick?: React.MouseEventHandler<HTMLSpanElement>
  labelHref?: string
  icon?: React.ReactNode
  labelClassName?: string
} & VariantProps<typeof $label>

export const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  (
    {
      children,
      className,
      blocked,
      horizontal,
      untitled,
      label,
      labelHref,
      onLabelDoubleClick,
      size,
      icon,
      labelClassName,
      ...rest
    },
    ref
  ) => {
    const LabelElement = labelHref ? Link : 'span'
    return (
      <label
        {...rest}
        ref={ref}
        className={$label({
          className,
          blocked,
          horizontal,
          untitled,
          size,
        })}
      >
        <LabelElement
          className={clsx(
            'flex min-w-max items-center select-none [&>span:last-of-type]:font-medium [&>span:last-of-type]:truncate',
            labelHref && 'cursor-default hover:underline',
            labelClassName
          )}
          onDoubleClick={onLabelDoubleClick}
          {...(labelHref && ({ href: labelHref } as any))}
        >
          {icon && <span className="mr-1.5 shrink-0 icon">{icon}</span>}
          {typeof label === 'string' ? <span>{label}</span> : label}
        </LabelElement>
        {children}
      </label>
    )
  }
)

type LabeledInputProps = LabelProps & {
  name: string
  inputProps: Omit<InputProps, 'name'>
  labelClassName?: string
}

export const LabeledInput = React.forwardRef<
  HTMLInputElement,
  LabeledInputProps
>(
  (
    {
      label,
      name,
      horizontal,
      untitled,
      blocked = true,
      inputProps,
      labelClassName,
      ...rest
    },
    ref
  ) => {
    const reactId = React.useId()

    return (
      <Label
        htmlFor={inputProps.id ?? reactId}
        draggable={blocked === true}
        label={label}
        horizontal={horizontal}
        untitled={untitled}
        blocked={blocked}
        className={labelClassName}
        {...rest}
      >
        <Input
          ref={ref}
          id={inputProps.id ?? reactId}
          name={name}
          {...inputProps}
        />
      </Label>
    )
  }
)

export const LabeledTextarea = React.forwardRef<
  HTMLTextAreaElement,
  LabeledInputProps
>(
  (
    {
      id,
      name,
      size,
      inputProps: { className, ...restInputProps },
      label,
      labelClassName,
    },
    ref
  ) => {
    return (
      <LabeledInput
        className={clsx('pt-2.5 !pb-2', labelClassName)}
        htmlFor={id}
        label={<span className="items-center inline-flex">{label}</span>}
        size={size}
        name={name}
        blocked={false}
        inputProps={{
          ...restInputProps,
          asChild: true,
          id,
          size,
          className: clsx('scroll-py-2 py-2 min-h-18', className),
          children: (
            <textarea
              ref={ref}
              className="min-h-20 scroll-py-2 max-h-44 px-3"
            />
          ),
        }}
      />
    )
  }
)

export const LabeledInputWithPresets = ({
  name,
  className,
  trigger,
  presets,
  inputProps,
  onSelectPreset,
  ...rest
}: LabeledInputProps & {
  presets: Array<{ title: string; value: string }>
  trigger?: React.ReactNode
  onSelectPreset: (value: string) => void
}) => {
  const reactId = React.useId()

  return (
    <Label
      htmlFor={inputProps.id ?? reactId}
      className={clsx(className, 'relative [&>span]:!pb-0.5')}
      {...rest}
    >
      <DropdownMenu>
        <div className="relative">
          <Input
            name={name}
            autoComplete="off"
            {...inputProps}
            className={clsx(
              'pr-5',
              clsx(
                inputProps.disabled && 'cursor-not-allowed',
                inputProps?.className
              )
            )}
          />
          <DropdownMenuTrigger
            asChild
            disabled={inputProps.disabled}
            className="absolute right-0.5 top-1/2 -translate-y-1/2 p-0.5"
          >
            {trigger ?? (
              <IconButton>
                <CaretSortIcon />
              </IconButton>
            )}
          </DropdownMenuTrigger>

          <DropdownMenuContent
            forceMount
            side="bottom"
            sideOffset={8}
            align="end"
            className="flex flex-col max-h-[calc(80vh-var(--header-height))]"
          >
            {presets.length ? (
              presets.map((preset, index) => (
                <DropdownMenuItem
                  onSelect={() => onSelectPreset(preset.value)}
                  className="h-6"
                  key={(preset.value ?? preset.title) + '-' + index}
                >
                  {preset.title}
                  <span className="text-dark-muted ml-1 text-xs max-w-[20ch] truncate">
                    {preset.value}
                  </span>
                </DropdownMenuItem>
              ))
            ) : (
              <p className="font-medium text-dark-muted text-xs">No results.</p>
            )}
          </DropdownMenuContent>
        </div>
      </DropdownMenu>
    </Label>
  )
}
